import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import {
	IBAN_COMPONENT_ELEMENT_ID,
	TEST_ELEMENT_IDS,
} from '@dealstack/shared/tests/testElementIds';
import { Button, Flex, Select } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
	PatchShareholderInvestorBankAccountDto,
	ShareholderInvestorPersonBankAccountsRes,
} from '@dealstack/shared/lib/generated';
import { BankAccountFormVerificationDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountFormVerificationDrawer.tsx';
import { BankAccountTile } from '@dealstack/frontend/shared/components/BankAccountTile/BankAccountTile.tsx';
import { useBankAccountWithCodeValidation } from '@dealstack/frontend/shared/components/BankAccountWithCodeValidation/useBankAccountWithCodeValidation.ts';
import { BANK_ACCOUNT_STATE } from '@dealstack/frontend/shared/components/BankAccountWithCodeValidation/useBankAccountWithCodeValidation.types.ts';
import {
	isBankAccountConfirmationPending,
	shouldShowInvestorStatusBadge,
} from '@dealstack/frontend/shared/components/BankAccountWithCodeValidation/useBankAccountWithCodeValidation.utils.ts';
import { BankAccountFormValues } from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';
import { toBankAccountPayload } from '@dealstack/frontend/shared/components/BankAccountForm/BankAccountForm.utils.ts';
import type { BankAccountVerifyFn } from '@dealstack/frontend/shared/components/BankAccountForm/useBankAccountForm.types.ts';
import {
	upsertBankAccount,
	validateShareholderBankAccount,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investor.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';

interface Props {
	disableNoAccountAlert?: boolean;
	onMutationFinish?: () => void;
	personId?: string;
	dealId?: string;
	bankAccountsData: ShareholderInvestorPersonBankAccountsRes[];
	bankAccountsLoading: boolean;
	refetchBankAccounts: () => void;
	dataTestId?: string;
	inferredCountryCode?: string;
}

type EditedBankAccount = ShareholderInvestorPersonBankAccountsRes['bankAccounts'][number] | null;

export const BankAccountSectionWithCodeValidation = ({
	disableNoAccountAlert,
	onMutationFinish,
	dealId,
	personId,
	bankAccountsData,
	bankAccountsLoading,
	refetchBankAccounts,
	dataTestId,
	inferredCountryCode,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	const {
		magicCode,
		magicCodeError,
		pageMode,
		setPageMode,
		setMagicCodeError,
		setMagicCode,
		pendingBankAccountId,
		setPendingBankAccountId,
		requestBankAccountCode,
		requestBankAccountCodeLoading,
		confirmBankAccount,
		confirmBankAccountLoading,
		upsertBankAccountRequest,
		upsertBankAccountRequestLoading,
	} = useBankAccountWithCodeValidation();

	const [editedBankAccount, setEditedBankAccount] = useState<EditedBankAccount>(null);
	const [currentPersonId, setCurrentPersonId] = useState<string>();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const isDrawerOpenRef = useRef(false);
	const openDrawer = (bankAccount: EditedBankAccount = null) => {
		setEditedBankAccount(bankAccount);
		isDrawerOpenRef.current = true;
		setIsDrawerOpen(true);
	};
	// Draft shell created lazily on the first IBAN lookup; the final save updates it.
	const shellBankAccountIdRef = useRef<string | null>(null);

	const shouldShowDealSelect = !personId && !dealId;
	const isInCodeMode = pageMode === BANK_ACCOUNT_STATE.CODE;

	const userPersonDeals = useMemo(
		() =>
			(bankAccountsData ?? []).map((data) => ({
				label: data.dealName,
				value: data.personId,
			})),
		[bankAccountsData]
	);

	useEffect(() => {
		refetchBankAccounts();
	}, [personId]);

	useEffect(() => {
		if (personId) {
			setCurrentPersonId(personId);
			return;
		}

		if (!currentPersonId && bankAccountsData?.length) {
			const currentBankAccount = dealId
				? bankAccountsData.find((data) => data.dealId === dealId)
				: bankAccountsData[0];
			setCurrentPersonId(currentBankAccount?.personId);
		}
	}, [bankAccountsData, dealId, personId]);

	const bankAccounts = useMemo(
		() =>
			(bankAccountsData ?? []).find((data) => data.personId === currentPersonId)
				?.bankAccounts ?? [],
		[currentPersonId, bankAccountsData]
	);

	// Closing always refetches so a draft created mid-flow still shows up after an
	// abandon; success paths close through here too, one fetch per drawer session.
	const closeDrawer = () => {
		isDrawerOpenRef.current = false;
		setIsDrawerOpen(false);
		setEditedBankAccount(null);
		setPageMode(BANK_ACCOUNT_STATE.FORM);
		setMagicCode('');
		setMagicCodeError('');
		setPendingBankAccountId(null);
		shellBankAccountIdRef.current = null;
		refetchBankAccounts();
	};

	// Ensures the draft account exists for this person, then runs the scoped lookup so
	// the attempt is logged against it.
	const verifyIdentifiers: BankAccountVerifyFn = async (dto, { countryCode }) => {
		let accountId = editedBankAccount?.id ?? shellBankAccountIdRef.current;
		if (!accountId) {
			const created = await upsertBankAccount({
				body: { countryCode: countryCode ?? undefined },
				personId: currentPersonId ?? '',
			});
			accountId = created.payload?.id ?? null;
			if (!accountId) {
				throw new Error('Failed to create the bank account');
			}
			shellBankAccountIdRef.current = accountId;
		}
		return await validateShareholderBankAccount({ bankAccountId: accountId, body: dto });
	};

	const requestConfirmationCode = (bankAccountId: string) => {
		requestBankAccountCode(
			{ bankAccountId },
			{
				onSuccess: () => {
					// The request outlives the drawer. Closing resets to FORM, so without
					// this a late success would put a closed drawer back into CODE mode —
					// the next open would land on the code screen for an abandoned account
					// and commit it.
					if (!isDrawerOpenRef.current) {
						return;
					}
					setPendingBankAccountId(bankAccountId);
					setPageMode(BANK_ACCOUNT_STATE.CODE);
				},
				// The save has already succeeded at this point — only the code email
				// failed. Saving again re-requests a code for the still-pending account.
				onError: () => {
					showToastV2({
						title: tCommonUi('profile.banking_details.request_code.error'),
						state: 'error',
					});
				},
			}
		);
	};

	const submitVerificationCode = async () => {
		setMagicCodeError('');
		if (!pendingBankAccountId || !magicCode) {
			showToastV2({
				title: tCommonUi('profile.banking_details.save_new.error'),
				state: 'error',
			});
			return;
		}

		await confirmBankAccount(
			{ bankAccountId: pendingBankAccountId, magicCode },
			{
				onError: () => {
					setMagicCodeError(tCommonUi('profile.banking_details.enter_code.error'));
				},
				onSuccess: (response) => {
					if (response.payload?.confirmed) {
						closeDrawer();

						showToastV2({
							title: tCommonUi('profile.banking_details.save_new.success'),
							dataTestId: IBAN_COMPONENT_ELEMENT_ID.UPDATE_BANK_ACCOUNT_SUCCESS_TOAST,
						});
						onMutationFinish?.();
					} else {
						setMagicCode('');
						showToastV2({
							title: tCommonUi('profile.banking_details.verification_code.invalid'),
							state: 'error',
							dataTestId: IBAN_COMPONENT_ELEMENT_ID.INVALID_CODE_TOAST,
						});
					}
				},
			}
		);
	};

	// The account saves first (resting unconfirmed), then the emailed code confirms it.
	// Without the code round the save itself submits the account for admin review.
	const submitBankAccountDetails = async (body: PatchShareholderInvestorBankAccountDto) => {
		upsertBankAccountRequest(
			{
				body,
				personId: currentPersonId ?? '',
			},
			{
				onSuccess: (response) => {
					const savedId =
						response.payload?.id ?? body.id ?? shellBankAccountIdRef.current;
					shellBankAccountIdRef.current = null;
					if (!savedId) {
						showToastV2({
							title: tCommonUi('profile.banking_details.save_new.error'),
							state: 'error',
						});
						return;
					}
					if (!isBankAccountConfirmationPending(response.payload?.status)) {
						closeDrawer();
						showToastV2({
							title: tCommonUi('profile.banking_details.save_new.success'),
							dataTestId: IBAN_COMPONENT_ELEMENT_ID.UPDATE_BANK_ACCOUNT_SUCCESS_TOAST,
						});
						onMutationFinish?.();
						return;
					}
					requestConfirmationCode(savedId);
				},
				onError: () => {
					setMagicCode('');
					setPageMode(BANK_ACCOUNT_STATE.FORM);
					showToastV2({
						title: tCommonUi('profile.banking_details.save_new.error'),
						state: 'error',
					});
				},
			}
		);
	};

	const handleBankAccountFormSubmit = async (
		formValues: BankAccountFormValues,
		bankAccountId?: string
	) => {
		const payload = toBankAccountPayload(formValues);

		if (!payload) {
			return;
		}

		void submitBankAccountDetails({
			...payload,
			id: bankAccountId ?? shellBankAccountIdRef.current ?? undefined,
		});
	};

	if (bankAccountsLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	return (
		<Flex direction="column" gap="1rem" data-testid={dataTestId}>
			<Flex align="flex-end" gap="0.5rem" w="100%">
				{shouldShowDealSelect ? (
					<Select
						label={tCommonUi('profile.banking_details.deal_name.label')}
						data={userPersonDeals}
						value={currentPersonId}
						disabled={isInCodeMode}
						onChange={(value) => setCurrentPersonId(value ?? undefined)}
						size="md"
						style={{ flex: 1 }}
					/>
				) : null}
				<Button
					variant="outline"
					w="fit-content"
					h="40px"
					onClick={() => openDrawer()}
					data-testid={TEST_ELEMENT_IDS.PROFILE_BANK_ACCOUNT_ADD_BUTTON}
				>
					{tCommonUi('profile.banking_details.add_button')}
				</Button>
			</Flex>

			{!disableNoAccountAlert && bankAccounts.length === 0 && (
				<InfoBox
					iconVariant="information"
					title={tCommonUi('profile.banking_details.no_account_alert.title')}
					description={tCommonUi('profile.banking_details.no_account_alert.text')}
					dataTestId={IBAN_COMPONENT_ELEMENT_ID.NO_ACCOUNT_ALERT_WRAPPER}
				/>
			)}

			<>
				{bankAccounts.map((bankAccount) => (
					<BankAccountTile
						key={bankAccount.id}
						bankAccount={bankAccount}
						hasEditPermission
						withBorder
						showStatusBadge={shouldShowInvestorStatusBadge(bankAccount.status)}
						statusBadgeTestId={TEST_ELEMENT_IDS.CAP_BANK_ACCOUNT_TILE}
						tileTestId={TEST_ELEMENT_IDS.PROFILE_BANK_ACCOUNT_TILE}
						editBtnTestId={TEST_ELEMENT_IDS.EDITABLE_TILE_EDIT_BUTTON}
						onEdit={() => openDrawer(bankAccount)}
					/>
				))}

				<BankAccountFormVerificationDrawer
					opened={isDrawerOpen}
					onClose={closeDrawer}
					title={
						editedBankAccount
							? tCommonUi('profile.banking_details.drawer.edit_title')
							: tCommonUi('profile.banking_details.drawer.add_title')
					}
					hasPermission
					bankAccount={editedBankAccount}
					isRequestLoading={
						upsertBankAccountRequestLoading || requestBankAccountCodeLoading
					}
					onSubmit={handleBankAccountFormSubmit}
					inferredCountryCode={inferredCountryCode}
					verifyIdentifiers={verifyIdentifiers}
					verification={
						isInCodeMode
							? {
									title: tCommonUi('profile.banking_details.drawer.verify_title'),
									label: tCommonUi('profile.banking_details.enter_code.title'),
									submitLabel: tCommonUi(
										'profile.banking_details.enter_code.submit_button'
									),
									code: magicCode,
									onCodeChange: setMagicCode,
									errorMessage: magicCodeError,
									onVerify: () => void submitVerificationCode(),
									isVerifying: confirmBankAccountLoading,
								}
							: null
					}
				/>
			</>
		</Flex>
	);
};
